import * as Sentry from "@sentry/browser";
import { API_BASE_URL, ENVIRONMENT, getVersionTag, SENTRY_KEY, SENTRY_PROJECT_ID, SESSION_ID } from "../../config";
import { debounce, hasValue, takeId } from "shared";
import { getBestName } from "../../util/user-greeting";
import { TrackerService, TrackerServiceProps } from "./tracker-service";

export class SentryService implements TrackerService {
	setProps(props: TrackerServiceProps): Promise<void> | void {
		debounce(
			() => {
				const { user, workspace } = props;
				Sentry.setUser(
					user != null
						? {
								id: takeId(user),
								username: getBestName(user.name),
								segment: workspace != null ? takeId(workspace) : undefined,
								...(workspace != null
									? [
											{
												plans: workspace.plan
											}
									  ]
									: [])
						  }
						: null
				);
			},
			"sentry-set-user",
			200
		);
	}

	/**
	 *  Initializes the sentry service.
	 */
	setup(props: TrackerServiceProps) {
		Sentry.init({
			dsn: `https://${SENTRY_KEY}.ingest.sentry.io/${SENTRY_PROJECT_ID}`,
			tracePropagationTargets: ["localhost", new RegExp(`^${API_BASE_URL}`)],
			integrations: [new Sentry.BrowserTracing()],
			environment: ENVIRONMENT,
			tracesSampleRate: 0.7,
			replaysSessionSampleRate: 0.1,
			replaysOnErrorSampleRate: 1.0,
			beforeBreadcrumb(breadcrumb, hint) {
				// Rewrite ui clicks to pierce the shadow dom
				if (breadcrumb.category === "ui.click" && hint?.event?.composedPath != null) {
					const path = hint.event
						.composedPath()
						.reverse()
						.reduce((acc: string[], target: HTMLElement) => {
							if (target.tagName != null) {
								acc.push(`${target.tagName.toLowerCase()}${hasValue(target.id) ? `#${target.id}` : ""}`);
							}

							return acc;
						}, [])
						.join(" > ");

					return { ...breadcrumb, message: path };
				}
				return breadcrumb;
			}
		});

		Sentry.configureScope((scope) => {
			scope.setTag("session_id", SESSION_ID);
			scope.setTag("version", getVersionTag());
		});

		this.setProps(props);
	}

	/**
	 * Tracks an event.
	 * @param name
	 * @param data
	 * @param props
	 */
	trackEvent(name: string, { data, props }: { data?: Object; props?: TrackerServiceProps }) {
		Sentry.addBreadcrumb({
			category: name.split(".")?.[0] ?? "general",
			message: name,
			level: "info"
		});
	}
}

export const sentryService = new SentryService();
